// src/components/onboarding/StepBio.js
import React, { useState } from 'react';
import { MessageCircle, Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BIO_PROMPTS } from '../../constants';

const StepBio = ({ data, onChange, errors }) => {
  const { t } = useTranslation();
  const prompts = data.bioPrompts || [];
  const [selectedPrompt, setSelectedPrompt] = useState('');

  const usedPrompts = prompts.map((p) => p.prompt);
  const availablePrompts = BIO_PROMPTS.filter((p) => !usedPrompts.includes(p));

  const addPrompt = () => {
    if (!selectedPrompt || prompts.length >= 3) return;
    onChange({
      bioPrompts: [...prompts, { prompt: selectedPrompt, answer: '' }],
    });
    setSelectedPrompt('');
  };

  const updateAnswer = (index, answer) => {
    const updated = prompts.map((p, i) => (i === index ? { ...p, answer } : p));
    onChange({ bioPrompts: updated });
  };

  const removePrompt = (index) => {
    onChange({ bioPrompts: prompts.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full mx-auto flex items-center justify-center text-white mb-3">
          <MessageCircle size={28} />
        </div>
        <h2 className="text-2xl font-bold dark:text-gray-100">{t('stepBio.title')}</h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">{t('stepBio.subtitle1')}</p>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">{t('stepBio.subtitle2')}</p>
      </div>

      {prompts.map((bp, i) => (
        <div key={i} className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-4 relative">
          <button
            type="button"
            onClick={() => removePrompt(i)}
            className="absolute top-3 right-3 text-gray-400 hover:text-red-500 transition"
          >
            <X size={16} />
          </button>
          <p className="text-sm font-semibold text-rose-600 dark:text-rose-400 mb-2">
            {t(bp.prompt)}
          </p>
          <textarea
            value={bp.answer}
            onChange={(e) => updateAnswer(i, e.target.value)}
            maxLength={200}
            rows={2}
            placeholder={t('stepBio.answerPlaceholder')}
            className="w-full px-3 py-2 bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500 rounded-xl dark:text-gray-100 focus:outline-none transition text-sm resize-none"
          />
          <p className="text-xs text-gray-400 text-right mt-1">{bp.answer.length}/200</p>
          {errors[`bioPrompt_${i}`] && (
            <p className="text-red-500 text-xs mt-1">{errors[`bioPrompt_${i}`]}</p>
          )}
        </div>
      ))}

      {prompts.length < 3 && availablePrompts.length > 0 && (
        <div className="flex gap-2">
          <select
            value={selectedPrompt}
            onChange={(e) => setSelectedPrompt(e.target.value)}
            className="flex-1 px-3 py-2.5 bg-gray-50 dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500 rounded-xl dark:text-gray-100 text-sm cursor-pointer"
          >
            <option value="">{t('stepBio.selectPrompt')}</option>
            {availablePrompts.map((p) => (
              <option key={p} value={p}>
                {t(p)}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={addPrompt}
            disabled={!selectedPrompt}
            className="px-3 py-2.5 bg-rose-500 text-white rounded-xl disabled:opacity-40 hover:bg-rose-600 transition"
          >
            <Plus size={18} />
          </button>
        </div>
      )}
    </div>
  );
};

export default StepBio;
